import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import BrokerageHeader from "@/components/BrokerageHeader";
import AppLink from "@/components/AppLink";
import {
  useAddBrokerageManager,
  useManagedBrokerage,
  useRemoveBrokerageManager,
  useSaveBrokerageBranding,
  useSaveFeaturedAgents,
  useSaveHiddenAgents,
  useSaveBrokerageTeams,
} from "@/hooks/useBrokerageManage";
import { LOGO_TYPES, uploadBrokerageLogo } from "@/services/brokerageService";
import { ABOUT_MAX, FIELD_ERRORS, aboutErrorOf, draftOf, isDirty } from "./model";
import { AgentsOnPage, PageEditors } from "./BrokeragePeople";
import { TeamsOnPage } from "./BrokerageTeams";
import "./BrokeragePageEditor.scss";

const CameraIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <path
      d="M3 6.5h3l1.4-2h5.2l1.4 2h3v9H3z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="10.8" r="2.8" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const BrokeragePageEditor = ({ selectedId, switcher = null }) => {
  const query = useManagedBrokerage(selectedId);
  const data = query.data;
  const saveBranding = useSaveBrokerageBranding(selectedId);
  const saveFeatured = useSaveFeaturedAgents(selectedId);
  const saveHidden = useSaveHiddenAgents(selectedId);
  const saveTeams = useSaveBrokerageTeams(selectedId);
  const addManager = useAddBrokerageManager(selectedId);
  const removeManager = useRemoveBrokerageManager(selectedId);
  const [draft, setDraft] = useState(draftOf(null));
  const [uploading, setUploading] = useState("");
  const logoRef = useRef(null);
  const coverRef = useRef(null);
  useEffect(() => {
    if (data) setDraft(draftOf(data.settings));
  }, [data?.id]);

  if (query.isLoading || !data) {
    return query.isError ? (
      <div className="brokerage-studio__state">
        Your brokerage page did not load.{" "}
        <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => query.refetch()}>
          Try again
        </button>
      </div>
    ) : (
      <div className="brokerage-studio__state" aria-busy="true">
        Loading your brokerage page…
      </div>
    );
  }

  const fieldErrors = saveBranding.error?.fields || {};
  const dirty = isDirty(draft, data.settings);
  const set = (key) => (event) => setDraft({ ...draft, [key]: event.target.value });
  const pageHref = data.network ? `/agents?b=${encodeURIComponent(data.name)}&n=${data.network}` : "";
  const previewBrokerage = {
    name: data.name,
    logoUrl: draft.logoUrl || data.brandLogoUrl,
    coverUrl: draft.coverUrl,
    about: draft.about,
    website: draft.website ? (/^https?:\/\//i.test(draft.website) ? draft.website : `https://${draft.website}`) : "",
    phone: draft.phone,
  };

  const onImage = (kind) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(kind);
    try {
      const url = await uploadBrokerageLogo(data.id, file, kind);
      setDraft((prev) => ({ ...prev, [kind === "cover" ? "coverUrl" : "logoUrl"]: url }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading("");
    }
  };

  const save = () =>
    saveBranding.mutate(
      {
        logoUrl: draft.logoUrl || null,
        coverUrl: draft.coverUrl || null,
        about: draft.about,
        website: draft.website,
        phone: draft.phone,
        iabsUrl: draft.iabsUrl,
      },
      {
        onSuccess: (saved) => {
          setDraft(draftOf(saved?.settings));
          toast.success("Your brokerage page is updated.");
        },
        onError: (error) => {
          if (!error.fields) toast.error(error.message);
        },
      },
    );

  const editSlots = {
    cover: (
      <div className="brokerage-studio__cover-tools">
        <button
          type="button"
          className="brokerage-studio__overlay"
          disabled={Boolean(uploading)}
          onClick={() => coverRef.current?.click()}
        >
          <CameraIcon />
          {uploading === "cover" ? "Uploading…" : draft.coverUrl ? "Change cover" : "Add a cover image"}
        </button>
        {draft.coverUrl ? (
          <button
            type="button"
            className="brokerage-studio__overlay is-quiet"
            onClick={() => setDraft({ ...draft, coverUrl: "" })}
          >
            Remove
          </button>
        ) : null}
      </div>
    ),
    logo: (
      <button
        type="button"
        className="brokerage-studio__logo-tool"
        aria-label={draft.logoUrl ? "Change logo" : "Add your logo"}
        disabled={Boolean(uploading)}
        onClick={() => logoRef.current?.click()}
      >
        <CameraIcon />
      </button>
    ),
  };

  return (
    <div className="brokerage-studio">
      {data.brandingHidden ? (
        <p className="brokerage-studio__paused" role="status">
          Gitcha has paused your custom branding on the public page. Contact support to restore&nbsp;it.
        </p>
      ) : null}
      {switcher}
      <section className="content-block brokerage-studio__canvas">
        <div className="title-sec">
          <h4>Your brokerage page</h4>
          {pageHref ? (
            <AppLink href={pageHref} target="_blank" rel="noopener noreferrer">
              View page
            </AppLink>
          ) : null}
        </div>
        <BrokerageHeader brokerage={previewBrokerage} name={data.name} preview editSlots={editSlots} />
        <input ref={coverRef} type="file" accept={Object.keys(LOGO_TYPES).join(",")} className="d-none" onChange={onImage("cover")} />
        <input ref={logoRef} type="file" accept={Object.keys(LOGO_TYPES).join(",")} className="d-none" onChange={onImage("logo")} />
        <div className="brokerage-studio__canvas-foot">
          <p className="brokerage-studio__hint">
            Cover: JPG or PNG, 1600 × 400 or wider, up to 5&nbsp;MB. Logo: PNG with a transparent background, up to 2&nbsp;MB.
            {!draft.logoUrl && data.brandLogoUrl ? ` Showing the ${data.brandName} logo until you add your own.` : ""}
          </p>
          {fieldErrors.logoUrl || fieldErrors.coverUrl ? (
            <p className="brokerage-studio__error">
              {fieldErrors.coverUrl ? FIELD_ERRORS.coverUrl : FIELD_ERRORS.logoUrl}
            </p>
          ) : null}
        </div>
      </section>

      <section className="content-block">
        <div className="title-sec">
          <h4>About and contact</h4>
        </div>
        <label className="brokerage-studio__label">
          About
          <textarea
            aria-invalid={Boolean(fieldErrors.about)}
            className="brokerage-studio__textarea"
            value={draft.about}
            onChange={set("about")}
            maxLength={ABOUT_MAX}
            rows={4}
            placeholder={`${data.name} has helped buyers and sellers in our community since…`}
          />
          <span className="brokerage-studio__count">
            {draft.about.length}/{ABOUT_MAX}
          </span>
        </label>
        {fieldErrors.about ? (
          <p className="brokerage-studio__error" role="alert">
            {aboutErrorOf(fieldErrors.about, fieldErrors.aboutTerm)}
          </p>
        ) : null}
        <div className="brokerage-studio__fields">
          <label className="brokerage-studio__label">
            Website
            <input value={draft.website} onChange={set("website")} placeholder="yourbrokerage.com" inputMode="url" />
            {fieldErrors.website ? <span className="brokerage-studio__error">{FIELD_ERRORS.website}</span> : null}
          </label>
          <label className="brokerage-studio__label">
            Office phone
            <input value={draft.phone} onChange={set("phone")} placeholder="(317) 555-0100" inputMode="tel" />
            {fieldErrors.phone ? <span className="brokerage-studio__error">{FIELD_ERRORS.phone}</span> : null}
          </label>
          {data.networkState === "TX" || draft.iabsUrl ? (
            <label className="brokerage-studio__label brokerage-studio__field--wide">
              Completed IABS form link
              <input
                value={draft.iabsUrl}
                onChange={set("iabsUrl")}
                placeholder="https://yourbrokerage.com/iabs.pdf"
                inputMode="url"
                aria-describedby="brokerage-studio-iabs-hint"
              />
              <span id="brokerage-studio-iabs-hint" className="brokerage-studio__hint">
                Every agent bio at your office links this form, as TREC asks of Texas license holders. Leave it empty and
                bios link the blank form on the TREC&nbsp;site.
              </span>
              {fieldErrors.iabsUrl ? <span className="brokerage-studio__error">{FIELD_ERRORS.iabsUrl}</span> : null}
            </label>
          ) : null}
        </div>
      </section>

      <AgentsOnPage data={data} saveFeatured={saveFeatured} saveHidden={saveHidden} />
      <TeamsOnPage data={data} saveTeams={saveTeams} />
      <PageEditors data={data} addManager={addManager} removeManager={removeManager} />

      {dirty ? (
        <div className="brokerage-studio__savebar" role="region" aria-label="Unsaved changes">
          <span>You have changes that are not on your page&nbsp;yet.</span>
          <button type="button" className="brokerage-studio__discard" onClick={() => setDraft(draftOf(data.settings))}>
            Discard
          </button>
          <button
            type="button"
            className="btn btn-primary brokerage-studio__button"
            disabled={saveBranding.isPending || Boolean(uploading)}
            onClick={save}
          >
            {saveBranding.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default BrokeragePageEditor;
